import { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import api from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '../components/ui/dialog';
import {
  Calendar, Buildings, UsersFour, CurrencyInr, Gear, FileArchive,
  Plus, ArrowLeft, PencilSimple, LockKey, LockOpen,
} from '@phosphor-icons/react';

const TABS = [
  { key: 'daily',     title: 'Daily Summary',  icon: Calendar },
  { key: 'agencies',  title: 'Agencies',       icon: Buildings },
  { key: 'staff',     title: 'Staff',          icon: UsersFour },
  { key: 'collect',   title: 'Collect Amount', icon: CurrencyInr },
  { key: 'rate',      title: 'Rate Master',    icon: Gear },
  { key: 'settle',    title: 'Settlements',    icon: FileArchive },
];

const fmtInr = (n) => '₹' + Number(n || 0).toLocaleString('en-IN', { maximumFractionDigits: 2 });
const fmtDate = (iso) => {
  if (!iso) return '—';
  const d = new Date(iso);
  return d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
};
const fmtDT = (iso) => {
  if (!iso) return '—';
  const d = new Date(iso);
  return d.toLocaleString('en-IN', { day: '2-digit', month: 'short', year: '2-digit', hour: '2-digit', minute: '2-digit' });
};

export default function ChatLedgerPage() {
  const nav = useNavigate();
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState('daily');
  const [perms, setPerms] = useState({ can_write: false, is_owner: false });
  const [stats, setStats] = useState({ total_debit: 0, total_credit: 0, outstanding: 0, cylinders: 0, agency_count: 0 });

  const loadPerms = useCallback(async () => {
    try {
      const { data } = await api.get('/chat/ledger/permissions');
      setPerms(data);
    } catch { /* ignore */ }
  }, []);

  const loadStats = useCallback(async () => {
    try {
      const { data } = await api.get('/chat/ledger/stats');
      setStats(data);
    } catch { /* ignore */ }
  }, []);

  useEffect(() => {
    loadPerms();
    loadStats();
  }, [loadPerms, loadStats]);

  // Not authorized
  if (!['admin', 'owner', 'assistant', 'accountant'].includes(user?.role)) {
    return (
      <div className="p-8 text-center" data-testid="ledger-unauthorized">
        <h2 className="text-2xl font-bold mb-2">🔒 Access Denied</h2>
        <p className="text-gray-500">Only owner/assistant/CA can view the Chat Ledger.</p>
      </div>
    );
  }

  return (
    <div className="min-h-full" data-testid="chat-ledger-page">
      {/* Header */}
      <div className="mb-5 flex items-start justify-between gap-4">
        <div>
          <button
            onClick={() => nav('/chat')}
            className="text-xs text-gray-500 hover:text-emerald-700 flex items-center gap-1 mb-2"
            data-testid="back-to-chat-home"
          >
            <ArrowLeft size={14} /> Back to Chat Home
          </button>
          <div className="text-xs tracking-widest text-gray-500 font-semibold mb-1">AGENCY LEDGER</div>
          <h1 className="text-3xl sm:text-4xl font-bold text-gray-900 tracking-tight" style={{ fontFamily: 'Georgia, "Times New Roman", serif' }}>
            Chat-Driven Agency Ledger
          </h1>
          <p className="text-xs text-gray-500 mt-1">
            Har Bulk Paste → auto 2 ledger rows (Cylinder Rate + Convenience Fee). Role: <b className="text-emerald-700">{perms.is_owner ? 'Owner' : 'Assistant'}</b>
          </p>
        </div>
        {/* Stats strip */}
        <div className="hidden md:grid grid-cols-4 gap-2 text-right">
          <StatCard label="Cylinders" value={stats.cylinders} tone="gray" />
          <StatCard label="Total Billed" value={fmtInr(stats.total_debit)} tone="rose" />
          <StatCard label="Collected" value={fmtInr(stats.total_credit)} tone="emerald" />
          <StatCard label="Outstanding" value={fmtInr(stats.outstanding)} tone="amber" />
        </div>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200 overflow-x-auto -mx-1 px-1">
        <div className="flex gap-1 min-w-max">
          {TABS.map(t => {
            const Icon = t.icon;
            const active = activeTab === t.key;
            return (
              <button
                key={t.key}
                onClick={() => setActiveTab(t.key)}
                className={
                  'flex items-center gap-1.5 px-4 py-2.5 text-sm font-semibold border-b-2 transition-colors ' +
                  (active
                    ? 'border-emerald-600 text-emerald-700'
                    : 'border-transparent text-gray-500 hover:text-gray-800')
                }
                data-testid={`ledger-tab-${t.key}`}
              >
                <Icon size={16} weight={active ? 'fill' : 'regular'} />
                {t.title}
              </button>
            );
          })}
        </div>
      </div>

      {/* Tab panels */}
      <div className="mt-5">
        {activeTab === 'daily'    && <TabDaily    onReload={loadStats} />}
        {activeTab === 'agencies' && <TabAgencies onReload={loadStats} />}
        {activeTab === 'staff'    && <TabStaff    perms={perms} onReload={loadStats} />}
        {activeTab === 'collect'  && <TabCollect  perms={perms} onReload={loadStats} />}
        {activeTab === 'rate'     && <TabRateMaster perms={perms} />}
        {activeTab === 'settle'   && <TabSettlements perms={perms} onReload={loadStats} />}
      </div>
    </div>
  );
}

function StatCard({ label, value, tone }) {
  const toneMap = {
    gray: 'bg-gray-50 text-gray-900 border-gray-200',
    rose: 'bg-rose-50 text-rose-900 border-rose-200',
    emerald: 'bg-emerald-50 text-emerald-900 border-emerald-200',
    amber: 'bg-amber-50 text-amber-900 border-amber-200',
  };
  return (
    <div className={`rounded-lg border px-3 py-2 ${toneMap[tone] || toneMap.gray}`}>
      <div className="text-[10px] uppercase tracking-wider font-semibold opacity-70">{label}</div>
      <div className="text-base font-bold tabular-nums">{value}</div>
    </div>
  );
}

/* ─── TAB 1: Daily Summary ─── */
function TabDaily({ onReload }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/chat/ledger/daily-summary?days=60');
      setRows(data || []);
      onReload?.();
    } catch (e) {
      toast.error('Load failed');
    }
    setLoading(false);
  }, [onReload]);
  useEffect(() => { load(); }, [load]);

  const totals = useMemo(() => rows.reduce((acc, r) => ({
    cyl: acc.cyl + r.cylinders,
    rate: acc.rate + r.rate_total,
    fee: acc.fee + r.fee_total,
    grand: acc.grand + r.grand_total,
  }), { cyl: 0, rate: 0, fee: 0, grand: 0 }), [rows]);

  return (
    <div data-testid="tab-daily">
      {loading && <div className="text-gray-500 text-sm py-4">Loading…</div>}
      {!loading && rows.length === 0 && (
        <EmptyState title="No deliveries yet" sub="Bulk-paste karte hi yahan date-wise summary dikhega." />
      )}
      {!loading && rows.length > 0 && (
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <Th>Date</Th>
                <Th num>Cylinders</Th>
                <Th num>Rate Total</Th>
                <Th num>Fee Total</Th>
                <Th num>Grand Total</Th>
              </tr>
            </thead>
            <tbody>
              {rows.map(r => (
                <tr key={r.date} className="border-b border-gray-100 hover:bg-gray-50">
                  <Td bold>{r.date}</Td>
                  <Td num>{r.cylinders}</Td>
                  <Td num>{fmtInr(r.rate_total)}</Td>
                  <Td num>{fmtInr(r.fee_total)}</Td>
                  <Td num bold>{fmtInr(r.grand_total)}</Td>
                </tr>
              ))}
            </tbody>
            <tfoot className="bg-emerald-50 border-t border-emerald-200">
              <tr>
                <Td bold>TOTAL</Td>
                <Td num bold>{totals.cyl}</Td>
                <Td num bold>{fmtInr(totals.rate)}</Td>
                <Td num bold>{fmtInr(totals.fee)}</Td>
                <Td num bold>{fmtInr(totals.grand)}</Td>
              </tr>
            </tfoot>
          </table>
        </div>
      )}
    </div>
  );
}

/* ─── TAB 2: Agencies ─── */
function TabAgencies({ onReload }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/chat/ledger/agencies');
      setRows(data || []);
      onReload?.();
    } catch { toast.error('Load failed'); }
    setLoading(false);
  }, [onReload]);
  useEffect(() => { load(); }, [load]);

  return (
    <div data-testid="tab-agencies">
      {loading && <div className="text-gray-500 text-sm py-4">Loading…</div>}
      {!loading && rows.length === 0 && (
        <EmptyState title="No agency activity yet" sub="Bulk-paste ke baad agency-wise outstanding yahan dikhegi." />
      )}
      {!loading && rows.length > 0 && (
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <Th>Agency</Th>
                <Th num>Cylinders</Th>
                <Th num>Debit (Billed)</Th>
                <Th num>Credit (Received)</Th>
                <Th num>Outstanding</Th>
              </tr>
            </thead>
            <tbody>
              {rows.map(r => (
                <tr key={r.agency_id || r.agency_name} className="border-b border-gray-100 hover:bg-gray-50">
                  <Td bold>{r.agency_name || '—'}</Td>
                  <Td num>{r.cylinders}</Td>
                  <Td num>{fmtInr(r.debit)}</Td>
                  <Td num>{fmtInr(r.credit)}</Td>
                  <Td num bold className={r.outstanding > 0 ? 'text-rose-700' : 'text-emerald-700'}>
                    {fmtInr(r.outstanding)}
                  </Td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

/* ─── TAB 3: Staff Payments (manual entry) ─── */
function TabStaff({ perms, onReload }) {
  const [entries, setEntries] = useState([]);
  const [summary, setSummary] = useState([]);
  const [staffList, setStaffList] = useState([]);
  const [showAdd, setShowAdd] = useState(false);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/chat/ledger/staff-payments');
      setEntries(data.entries || []);
      setSummary(data.summary || []);
      onReload?.();
    } catch { toast.error('Load failed'); }
    setLoading(false);
  }, [onReload]);

  const loadStaff = useCallback(async () => {
    try {
      const { data } = await api.get('/chat/ledger/staff-list');
      setStaffList(data || []);
    } catch { /* ignore */ }
  }, []);

  useEffect(() => { load(); loadStaff(); }, [load, loadStaff]);

  return (
    <div data-testid="tab-staff" className="space-y-5">
      <div className="flex items-center justify-between">
        <h3 className="text-base font-semibold text-gray-900">Staff Payments</h3>
        {perms.can_write && (
          <Button size="sm" onClick={() => setShowAdd(true)} data-testid="add-staff-payment-btn">
            <Plus size={14} className="mr-1" /> Payment Add Karein
          </Button>
        )}
      </div>

      {/* Summary */}
      {summary.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          {summary.map(s => (
            <div key={s.staff_id} className="bg-white rounded-lg border border-gray-200 p-3">
              <div className="text-xs text-gray-500">Total Paid</div>
              <div className="text-lg font-bold text-emerald-700 tabular-nums">{fmtInr(s.total_paid)}</div>
              <div className="text-sm text-gray-800 mt-1">{s.staff_name}</div>
              <div className="text-[11px] text-gray-400">{s.count} payment{s.count > 1 ? 's' : ''}</div>
            </div>
          ))}
        </div>
      )}

      {/* Entries */}
      {loading && <div className="text-gray-500 text-sm py-4">Loading…</div>}
      {!loading && entries.length === 0 && (
        <EmptyState title="No staff payments yet" sub="Click 'Payment Add Karein' to log a cash/UPI payment to staff." />
      )}
      {!loading && entries.length > 0 && (
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <Th>Date</Th>
                <Th>Staff</Th>
                <Th num>Amount</Th>
                <Th>Mode</Th>
                <Th>Notes</Th>
                <Th>Paid By</Th>
              </tr>
            </thead>
            <tbody>
              {entries.map(e => (
                <tr key={e.id} className="border-b border-gray-100 hover:bg-gray-50">
                  <Td>{fmtDT(e.paid_at)}</Td>
                  <Td bold>{e.staff_name}</Td>
                  <Td num bold className="text-emerald-700">{fmtInr(e.amount)}</Td>
                  <Td className="uppercase text-xs">{e.mode}</Td>
                  <Td>{e.notes || '—'}</Td>
                  <Td className="text-xs text-gray-500">{e.paid_by_name}</Td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {showAdd && (
        <StaffPaymentDialog
          staffList={staffList}
          onClose={() => setShowAdd(false)}
          onSaved={() => { setShowAdd(false); load(); }}
        />
      )}
    </div>
  );
}

function StaffPaymentDialog({ staffList, onClose, onSaved }) {
  const [staffId, setStaffId] = useState('');
  const [amount, setAmount] = useState('');
  const [mode, setMode] = useState('cash');
  const [notes, setNotes] = useState('');
  const [paidAt, setPaidAt] = useState(new Date().toISOString().slice(0, 10));
  const [saving, setSaving] = useState(false);

  const save = async () => {
    if (!staffId) { toast.error('Staff select karein'); return; }
    const amt = parseFloat(amount);
    if (!amt || amt <= 0) { toast.error('Valid amount daalein'); return; }
    setSaving(true);
    try {
      await api.post('/chat/ledger/staff-payment', {
        staff_id: staffId, amount: amt, mode, notes,
        paid_at: paidAt ? new Date(paidAt).toISOString() : '',
      });
      toast.success('Payment added');
      onSaved?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed');
    }
    setSaving(false);
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent data-testid="staff-payment-dialog">
        <DialogHeader><DialogTitle>Add Staff Payment</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <Field label="Staff">
            <select
              className="w-full border rounded px-3 py-2 text-sm"
              value={staffId} onChange={e => setStaffId(e.target.value)}
              data-testid="staff-select"
            >
              <option value="">— Choose staff —</option>
              {staffList.map(s => <option key={s.id} value={s.id}>{s.name} ({s.role})</option>)}
            </select>
          </Field>
          <Field label="Amount (₹)">
            <Input type="number" value={amount} onChange={e => setAmount(e.target.value)} placeholder="5000" data-testid="staff-amount" />
          </Field>
          <Field label="Mode">
            <select className="w-full border rounded px-3 py-2 text-sm" value={mode} onChange={e => setMode(e.target.value)}>
              <option value="cash">Cash</option>
              <option value="upi">UPI</option>
              <option value="bank">Bank Transfer</option>
            </select>
          </Field>
          <Field label="Date">
            <Input type="date" value={paidAt} onChange={e => setPaidAt(e.target.value)} />
          </Field>
          <Field label="Notes (optional)">
            <Textarea value={notes} onChange={e => setNotes(e.target.value)} rows={2} />
          </Field>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={save} disabled={saving} data-testid="staff-payment-save">
            {saving ? 'Saving…' : 'Save Payment'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/* ─── TAB 4: Collect Amount (pending receivables) ─── */
function TabCollect({ perms, onReload }) {
  const [agencies, setAgencies] = useState([]);
  const [collections, setCollections] = useState([]);
  const [agencyList, setAgencyList] = useState([]);
  const [showAdd, setShowAdd] = useState(false);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [ag, col, rm] = await Promise.all([
        api.get('/chat/ledger/agencies'),
        api.get('/chat/ledger/collect'),
        api.get('/chat/ledger/rate-master'),
      ]);
      setAgencies(ag.data || []);
      setCollections(col.data || []);
      setAgencyList(rm.data || []);
      onReload?.();
    } catch { toast.error('Load failed'); }
    setLoading(false);
  }, [onReload]);

  useEffect(() => { load(); }, [load]);

  const totalOutstanding = useMemo(
    () => agencies.reduce((a, b) => a + (b.outstanding > 0 ? b.outstanding : 0), 0),
    [agencies]
  );
  const pendingAgencies = agencies.filter(a => a.outstanding > 0);

  return (
    <div data-testid="tab-collect" className="space-y-5">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-base font-semibold text-gray-900">Pending to Collect</h3>
          <p className="text-xs text-gray-500">
            Total Pending: <b className="text-rose-700 text-base">{fmtInr(totalOutstanding)}</b>
          </p>
        </div>
        {perms.can_write && (
          <Button size="sm" onClick={() => setShowAdd(true)} data-testid="add-collection-btn">
            <Plus size={14} className="mr-1" /> Collection Entry
          </Button>
        )}
      </div>

      {loading && <div className="text-gray-500 text-sm py-4">Loading…</div>}
      {!loading && pendingAgencies.length === 0 && (
        <EmptyState title="All clear! 🎉" sub="Koi agency se paisa pending nahi hai." />
      )}

      {!loading && pendingAgencies.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
          {pendingAgencies.map(a => (
            <div key={a.agency_id} className="bg-white rounded-lg border border-rose-200 p-4 hover:shadow-md transition">
              <div className="flex items-start justify-between mb-2">
                <div className="font-semibold text-gray-900">{a.agency_name}</div>
                <span className="text-[10px] bg-rose-100 text-rose-700 rounded px-1.5 py-0.5 font-semibold">PENDING</span>
              </div>
              <div className="text-2xl font-bold text-rose-700 tabular-nums">{fmtInr(a.outstanding)}</div>
              <div className="text-[11px] text-gray-500 mt-1">
                Billed: {fmtInr(a.debit)} · Received: {fmtInr(a.credit)} · Cylinders: {a.cylinders}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Recent collections */}
      {!loading && collections.length > 0 && (
        <>
          <h4 className="text-sm font-semibold text-gray-700 mt-4">Recent Collections</h4>
          <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <Th>Date</Th>
                  <Th>Agency</Th>
                  <Th num>Amount</Th>
                  <Th>Mode</Th>
                  <Th>Notes</Th>
                  <Th>By</Th>
                </tr>
              </thead>
              <tbody>
                {collections.map(e => (
                  <tr key={e.id} className="border-b border-gray-100 hover:bg-gray-50">
                    <Td>{fmtDT(e.dispatched_at)}</Td>
                    <Td bold>{e.agency_name}</Td>
                    <Td num bold className="text-emerald-700">{fmtInr(e.amount)}</Td>
                    <Td className="uppercase text-xs">{e.mode}</Td>
                    <Td>{e.notes || '—'}</Td>
                    <Td className="text-xs text-gray-500">{e.created_by_name}</Td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}

      {showAdd && (
        <CollectDialog
          agencyList={agencyList}
          onClose={() => setShowAdd(false)}
          onSaved={() => { setShowAdd(false); load(); }}
        />
      )}
    </div>
  );
}

function CollectDialog({ agencyList, onClose, onSaved }) {
  const [agencyId, setAgencyId] = useState('');
  const [amount, setAmount] = useState('');
  const [mode, setMode] = useState('cash');
  const [notes, setNotes] = useState('');
  const [collectedAt, setCollectedAt] = useState(new Date().toISOString().slice(0, 10));
  const [saving, setSaving] = useState(false);

  const save = async () => {
    if (!agencyId) { toast.error('Agency select karein'); return; }
    const amt = parseFloat(amount);
    if (!amt || amt <= 0) { toast.error('Valid amount daalein'); return; }
    setSaving(true);
    try {
      await api.post('/chat/ledger/collect', {
        agency_id: agencyId, amount: amt, mode, notes,
        collected_at: collectedAt ? new Date(collectedAt).toISOString() : '',
      });
      toast.success('Collection recorded');
      onSaved?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed');
    }
    setSaving(false);
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent data-testid="collect-dialog">
        <DialogHeader><DialogTitle>Add Collection Entry</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <Field label="Agency">
            <select
              className="w-full border rounded px-3 py-2 text-sm"
              value={agencyId} onChange={e => setAgencyId(e.target.value)}
              data-testid="collect-agency-select"
            >
              <option value="">— Choose agency —</option>
              {agencyList.map(a => <option key={a.id} value={a.id}>{a.name}</option>)}
            </select>
          </Field>
          <Field label="Amount Received (₹)">
            <Input type="number" value={amount} onChange={e => setAmount(e.target.value)} placeholder="50000" data-testid="collect-amount" />
          </Field>
          <Field label="Mode">
            <select className="w-full border rounded px-3 py-2 text-sm" value={mode} onChange={e => setMode(e.target.value)}>
              <option value="cash">Cash</option>
              <option value="upi">UPI</option>
              <option value="bank">Bank Transfer</option>
              <option value="cheque">Cheque</option>
            </select>
          </Field>
          <Field label="Date">
            <Input type="date" value={collectedAt} onChange={e => setCollectedAt(e.target.value)} />
          </Field>
          <Field label="Notes (optional)">
            <Textarea value={notes} onChange={e => setNotes(e.target.value)} rows={2} placeholder="Ref no, UPI ID, etc." />
          </Field>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={save} disabled={saving} data-testid="collect-save">
            {saving ? 'Saving…' : 'Save Collection'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/* ─── TAB 5: Rate Master ─── */
function TabRateMaster({ perms }) {
  const [rows, setRows] = useState([]);
  const [editing, setEditing] = useState(null);
  const [loading, setLoading] = useState(false);
  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/chat/ledger/rate-master');
      setRows(data || []);
    } catch { toast.error('Load failed'); }
    setLoading(false);
  }, []);
  useEffect(() => { load(); }, [load]);

  return (
    <div data-testid="tab-rate">
      <div className="flex items-center justify-between mb-3">
        <div>
          <h3 className="text-base font-semibold text-gray-900">Rate Master</h3>
          <p className="text-xs text-gray-500">
            {perms.is_owner ? 'Edit karne par agency ki rate turant update ho jaayegi. Puraane ledger safe hain (snapshot pehle hi liya ja chuka hai).' : 'Only owner can edit. Aap view-only mode mein hain.'}
          </p>
        </div>
      </div>
      {loading && <div className="text-gray-500 text-sm py-4">Loading…</div>}
      {!loading && (
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <Th>Agency</Th>
                <Th>Oil Co.</Th>
                <Th num>Cylinder Rate</Th>
                <Th num>Convenience Fee</Th>
                <Th num>Total per Cyl</Th>
                <Th>Status</Th>
                {perms.is_owner && <Th>Action</Th>}
              </tr>
            </thead>
            <tbody>
              {rows.map(r => (
                <tr key={r.id} className="border-b border-gray-100 hover:bg-gray-50">
                  <Td bold>{r.name}</Td>
                  <Td className="text-xs uppercase">{r.oil_company || '—'}</Td>
                  <Td num>{fmtInr(r.rate)}</Td>
                  <Td num>{fmtInr(r.convenience_fee)}</Td>
                  <Td num bold>{fmtInr(r.rate + r.convenience_fee)}</Td>
                  <Td>
                    <span className={`text-[10px] rounded px-1.5 py-0.5 font-semibold ${r.status === 'active' ? 'bg-emerald-100 text-emerald-700' : 'bg-gray-200 text-gray-600'}`}>
                      {r.status}
                    </span>
                  </Td>
                  {perms.is_owner && (
                    <Td>
                      <Button
                        size="sm" variant="outline"
                        onClick={() => setEditing(r)}
                        data-testid={`edit-rate-${r.id}`}
                      >
                        <PencilSimple size={12} className="mr-1" /> Edit
                      </Button>
                    </Td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {editing && (
        <RateEditDialog
          row={editing}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); load(); }}
        />
      )}
    </div>
  );
}

function RateEditDialog({ row, onClose, onSaved }) {
  const [rate, setRate] = useState(String(row.rate || 0));
  const [cfee, setCfee] = useState(String(row.convenience_fee || 0));
  const [saving, setSaving] = useState(false);

  const save = async () => {
    setSaving(true);
    try {
      await api.patch(`/chat/ledger/rate-master/${row.id}`, {
        rate: parseFloat(rate) || 0,
        convenience_fee: parseFloat(cfee) || 0,
      });
      toast.success('Rate updated');
      onSaved?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed');
    }
    setSaving(false);
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent data-testid="rate-edit-dialog">
        <DialogHeader><DialogTitle>Edit Rate — {row.name}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <Field label="Cylinder Rate (₹)">
            <Input type="number" value={rate} onChange={e => setRate(e.target.value)} data-testid="rate-input" />
          </Field>
          <Field label="Convenience Fee (₹)">
            <Input type="number" value={cfee} onChange={e => setCfee(e.target.value)} data-testid="cfee-input" />
          </Field>
          <div className="text-xs text-gray-500 bg-amber-50 rounded border border-amber-200 px-3 py-2">
            ⚠️ Future bulk-paste dispatches ismein naya rate use karenge. Puraane ledger entries jaise the waise hi rahenge.
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={save} disabled={saving} data-testid="rate-save">
            {saving ? 'Saving…' : 'Save Rate'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/* ─── TAB 6: Settlements ─── */
function TabSettlements({ perms, onReload }) {
  const [rows, setRows] = useState([]);
  const [agencyList, setAgencyList] = useState([]);
  const [showNew, setShowNew] = useState(false);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [s, a] = await Promise.all([
        api.get('/chat/ledger/settlements'),
        api.get('/chat/ledger/rate-master'),
      ]);
      setRows(s.data || []);
      setAgencyList(a.data || []);
      onReload?.();
    } catch { toast.error('Load failed'); }
    setLoading(false);
  }, [onReload]);
  useEffect(() => { load(); }, [load]);

  const reopen = async (sid) => {
    if (!window.confirm('Settlement reopen karna hai? Entries phir se editable ho jaayengi.')) return;
    try {
      await api.post(`/chat/ledger/settlement/${sid}/reopen`);
      toast.success('Reopened');
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); }
  };

  return (
    <div data-testid="tab-settle">
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-base font-semibold text-gray-900">Settlements</h3>
        {perms.is_owner && (
          <Button size="sm" onClick={() => setShowNew(true)} data-testid="new-settlement-btn">
            <Plus size={14} className="mr-1" /> New Settlement
          </Button>
        )}
      </div>
      {loading && <div className="text-gray-500 text-sm py-4">Loading…</div>}
      {!loading && rows.length === 0 && (
        <EmptyState title="No settlements yet" sub="Period close karne ke liye 'New Settlement' button use karein." />
      )}
      {!loading && rows.length > 0 && (
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <Th>Agency</Th>
                <Th>Period</Th>
                <Th num>Entries</Th>
                <Th num>Debit</Th>
                <Th num>Credit</Th>
                <Th num>Net</Th>
                <Th>Status</Th>
                <Th>By</Th>
                {perms.is_owner && <Th>Action</Th>}
              </tr>
            </thead>
            <tbody>
              {rows.map(s => (
                <tr key={s.id} className="border-b border-gray-100 hover:bg-gray-50">
                  <Td bold>{s.agency_name}</Td>
                  <Td className="text-xs">{s.from_date} → {s.to_date}</Td>
                  <Td num>{s.entries_count}</Td>
                  <Td num>{fmtInr(s.total_debit)}</Td>
                  <Td num>{fmtInr(s.total_credit)}</Td>
                  <Td num bold className={s.net_balance > 0 ? 'text-rose-700' : 'text-emerald-700'}>
                    {fmtInr(s.net_balance)}
                  </Td>
                  <Td>
                    <span className={`text-[10px] rounded px-1.5 py-0.5 font-semibold inline-flex items-center gap-1 ${
                      s.status === 'closed' ? 'bg-gray-900 text-white' : 'bg-amber-100 text-amber-800'
                    }`}>
                      {s.status === 'closed' ? <LockKey size={10} /> : <LockOpen size={10} />}
                      {s.status.toUpperCase()}
                    </span>
                  </Td>
                  <Td className="text-xs text-gray-500">{s.closed_by_name}<br/><span className="text-[10px]">{fmtDate(s.closed_at)}</span></Td>
                  {perms.is_owner && (
                    <Td>
                      {s.status === 'closed' && (
                        <Button size="sm" variant="outline" onClick={() => reopen(s.id)} data-testid={`reopen-${s.id}`}>
                          Reopen
                        </Button>
                      )}
                    </Td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {showNew && (
        <NewSettlementDialog
          agencyList={agencyList}
          onClose={() => setShowNew(false)}
          onSaved={() => { setShowNew(false); load(); }}
        />
      )}
    </div>
  );
}

function NewSettlementDialog({ agencyList, onClose, onSaved }) {
  const today = new Date().toISOString().slice(0, 10);
  const firstOfMonth = (() => {
    const d = new Date(); d.setDate(1);
    return d.toISOString().slice(0, 10);
  })();
  const [agencyId, setAgencyId] = useState('');
  const [fromDate, setFromDate] = useState(firstOfMonth);
  const [toDate, setToDate] = useState(today);
  const [saving, setSaving] = useState(false);

  const save = async () => {
    if (!agencyId) { toast.error('Agency select karein'); return; }
    setSaving(true);
    try {
      await api.post('/chat/ledger/settlement', {
        agency_id: agencyId, from_date: fromDate, to_date: toDate,
      });
      toast.success('Settlement closed');
      onSaved?.();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); }
    setSaving(false);
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent data-testid="new-settlement-dialog">
        <DialogHeader><DialogTitle>New Settlement</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <Field label="Agency">
            <select
              className="w-full border rounded px-3 py-2 text-sm"
              value={agencyId} onChange={e => setAgencyId(e.target.value)}
              data-testid="settle-agency-select"
            >
              <option value="">— Choose agency —</option>
              {agencyList.map(a => <option key={a.id} value={a.id}>{a.name}</option>)}
            </select>
          </Field>
          <Field label="From Date">
            <Input type="date" value={fromDate} onChange={e => setFromDate(e.target.value)} />
          </Field>
          <Field label="To Date">
            <Input type="date" value={toDate} onChange={e => setToDate(e.target.value)} />
          </Field>
          <div className="text-xs text-gray-500 bg-amber-50 rounded border border-amber-200 px-3 py-2">
            ⚠️ Is period ki saari unsettled entries lock ho jaayengi. Owner baad mein reopen kar sakte hain.
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={save} disabled={saving} data-testid="settle-save">
            {saving ? 'Saving…' : 'Close Period'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/* ─── Shared UI atoms ─── */
function Th({ children, num }) {
  return <th className={`px-3 py-2.5 text-xs font-semibold text-gray-600 uppercase tracking-wider ${num ? 'text-right' : 'text-left'}`}>{children}</th>;
}
function Td({ children, num, bold, className = '' }) {
  return <td className={`px-3 py-2.5 ${num ? 'text-right tabular-nums' : ''} ${bold ? 'font-semibold' : ''} ${className}`}>{children}</td>;
}
function Field({ label, children }) {
  return (
    <label className="block">
      <span className="text-xs font-semibold text-gray-700 block mb-1">{label}</span>
      {children}
    </label>
  );
}
function EmptyState({ title, sub }) {
  return (
    <div className="bg-gray-50 rounded-xl border border-dashed border-gray-300 py-10 text-center">
      <div className="text-base font-semibold text-gray-700">{title}</div>
      {sub && <div className="text-xs text-gray-500 mt-1">{sub}</div>}
    </div>
  );
}
